/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Suspense } from 'react';

import { css, jsx } from '@compiled/react';
import type { IntlShape } from 'react-intl';

import type { RenderOptionsPropsT, Command, PublicPluginAPI } from '@atlaskit/editor-common/types';
import { token } from '@atlaskit/tokens';

import type { AgentManagedExtensionPlugin } from '../../../common/agentManagedExtensionPluginType';
import { AimoConfigurationLoadState } from '../AimoConfigurationLoadState';

import { createRetryHydrateSelectionCommand, createUpdateIntervalCommand } from './commands';
import { getAutoUpdateIntervalOptions } from './options';
import { useAutoUpdateIntervalState } from './useAutoUpdateIntervalState';

const menuStyles = css({
	boxSizing: 'border-box',
	paddingBlock: token('space.075'),
	paddingInline: token('space.075'),
	width: '100%',
});

const optionStyles = css({
	alignItems: 'center',
	backgroundColor: 'transparent',
	border: 0,
	borderRadius: token('radius.small'),
	boxSizing: 'border-box',
	color: token('color.text'),
	cursor: 'pointer',
	display: 'flex',
	gap: token('space.100'),
	minHeight: '36px',
	paddingBlock: token('space.075'),
	paddingInline: token('space.100'),
	textAlign: 'left',
	width: '100%',

	'&:hover': {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
	},

	'&:focus-visible': {
		outlineColor: token('color.border.focused'),
		outlineOffset: token('space.025'),
		outlineStyle: 'solid',
		outlineWidth: token('border.width.focused'),
	},
});

const selectedOptionStyles = css({
	backgroundColor: token('color.background.selected'),
	color: token('color.text.selected'),

	'&:hover': {
		backgroundColor: token('color.background.selected.hovered'),
	},
});

const optionIconStyles = css({
	display: 'flex',
	flexShrink: 0,
});

const optionIconFallbackStyles = css({
	display: 'inline-block',
	height: '24px',
	width: '24px',
});

const optionLabelStyles = css({
	font: token('font.body'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

type AutoUpdateIntervalMenuProps = {
	api?: PublicPluginAPI<[AgentManagedExtensionPlugin]>;
	dispatchCommand: RenderOptionsPropsT<Command>['dispatchCommand'];
	hide: RenderOptionsPropsT<Command>['hide'];
	intl: IntlShape;
	label: string;
};

export const AutoUpdateIntervalMenu = ({
	api,
	dispatchCommand,
	hide,
	intl,
	label,
}: AutoUpdateIntervalMenuProps): JSX.Element => {
	const { isFailed, isLoading, selectedUpdateInterval } = useAutoUpdateIntervalState(api);

	if (isLoading) {
		return (
			<div aria-label={label} css={menuStyles}>
				<AimoConfigurationLoadState
					status="loading"
					testId="agent-managed-extension-auto-update-interval-loading"
					variant="menu"
				/>
			</div>
		);
	}

	if (isFailed) {
		return (
			<div aria-label={label} css={menuStyles}>
				<AimoConfigurationLoadState
					onRetry={() => dispatchCommand(createRetryHydrateSelectionCommand())}
					status="failed"
					testId="agent-managed-extension-auto-update-interval-load-error"
					variant="menu"
				/>
			</div>
		);
	}

	return (
		<div aria-label={label} css={menuStyles} role="menu">
			{getAutoUpdateIntervalOptions(intl).map((option) => {
				const Icon = option.Icon;
				const isSelected = (selectedUpdateInterval ?? 'none') === option.option;
				return (
					<button
						aria-checked={isSelected}
						css={[optionStyles, isSelected && selectedOptionStyles]}
						data-testid={option.id}
						key={option.id}
						onClick={() => {
							dispatchCommand(createUpdateIntervalCommand(option.option));
							hide();
						}}
						role="menuitemcheckbox"
						type="button"
					>
						<span css={optionIconStyles}>
							<Suspense fallback={<span css={optionIconFallbackStyles} />}>
								<Icon label="" />
							</Suspense>
						</span>
						<span css={optionLabelStyles}>{option.label}</span>
					</button>
				);
			})}
		</div>
	);
};
